import { z } from "zod";
import { throughput, type PlanckSnapshot, type PlanckResult } from "./planck";
import { RELEASE_MODELS } from "@/components/photon/generated/release-data";

export type BenchmarkModality = "vision" | "speech";
export type PerformanceRow = {
  modelId: string;
  engine: string;
  concurrency: number;
  throughput?: number;
  latency?: {
    p50?: number | null;
    p95?: number | null;
    p99?: number | null;
  };
};
export type BenchmarkDataset = {
  id: string;
  benchmark: PlanckResult["benchmark"];
  modality: BenchmarkModality;
  device: string;
  unit: string;
  models: { id: string; model: string; family?: string }[];
  measurements: PerformanceRow[];
  results: PlanckResult[];
};

export const runtimeName = (engine: string) =>
  ({
    photon: "Photon",
    vllm: "vLLM",
    sglang: "SGLang",
    trtllm: "TensorRT-LLM",
    nemo: "NeMo",
  })[engine] ?? engine;

function modelDetails(source: string) {
  const name = source.split("/").pop()!.replace(/-(it|preview)$/i, "");
  const key = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const known = RELEASE_MODELS.find((model) => key(model.name) === key(name));
  if (known) return { model: known.name, family: known.family };
  return {
    model: name
      .replace(/^qwen(\d+(?:\.\d+)?)-/i, "Qwen $1 ")
      .replace(/^gemma-(\d+)-/i, "Gemma $1 ")
      .replace(/^moondream(\d+(?:\.\d+)?)-?/i, "Moondream $1 ")
      .trim(),
  };
}

export const workloadDescription = (benchmark: BenchmarkDataset["benchmark"]) =>
  benchmark === "describe"
    ? "Image description, forced to 256 tokens"
    : benchmark === "librispeech"
      ? "Transcription, variable length clips"
      : "ChartQA";

const metadata = z.object({
  platform: z
    .object({ device: z.object({ name: z.string() }) })
    .optional()
    .catch(undefined),
  engine: z.object({
    name: z.string(),
    packages: z
      .record(z.string(), z.object({ version: z.string() }))
      .optional()
      .catch(undefined),
  }),
});

export function benchmarkEnvironment(benchmark: BenchmarkDataset) {
  const records = benchmark.results.filter(({ summary }) => !summary.error);
  const details = records.map(({ summary }) => metadata.parse(summary));
  const dates = [...new Set(records.flatMap(({ summary }) => {
    const time = Date.parse(summary.generated_at ?? "");
    return Number.isFinite(time)
      ? [new Date(time).toISOString().slice(0, 10)]
      : [];
  }))].sort();
  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  return {
    gpu: [...new Set(details.flatMap(({ platform }) =>
      platform ? [platform.device.name] : [],
    ))].join(" · ") || benchmark.device,
    workload: workloadDescription(benchmark.benchmark),
    concurrency: [...new Set(benchmark.measurements.map((row) => row.concurrency))]
      .sort((a, b) => a - b),
    runtimes: [...new Set(details.map(({ engine }) => {
      const versions = Object.entries(engine.packages ?? {})
        .map(([name, pkg]) => `${name} ${pkg.version}`);
      return runtimeName(engine.name) +
        (versions.length ? ` (${versions.join(", ")})` : "");
    }))],
    date: dates.length === 0
      ? undefined
      : dates.length === 1
        ? formatDate(dates[0])
        : `${formatDate(dates[0])} – ${formatDate(dates[dates.length - 1])}`,
  };
}

export function benchmarksFromSnapshot(
  snapshot: PlanckSnapshot | null,
): BenchmarkDataset[] {
  const datasets = new Map<string, BenchmarkDataset>();
  for (const result of snapshot?.results ?? []) {
    const { benchmark, device, model, summary } = result;
    if (summary.error) continue;
    const id = `${benchmark}-${device}`;
    let dataset = datasets.get(id);
    if (!dataset) {
      dataset = {
        id,
        benchmark,
        device: device.toUpperCase(),
        modality: benchmark === "librispeech" ? "speech" : "vision",
        unit:
          benchmark === "librispeech"
            ? "audio s/s"
            : benchmark === "describe"
              ? "tok/s"
              : "req/s",
        models: [],
        measurements: [],
        results: [],
      };
      datasets.set(id, dataset);
    }
    dataset.results.push(result);
    if (!dataset.models.some(({ id }) => id === model)) {
      dataset.models.push({
        id: model,
        ...modelDetails(snapshot!.models[model] ?? model),
      });
    }
    for (const row of summary.measurements ?? []) {
      dataset.measurements.push({
        modelId: model,
        engine: summary.engine.name,
        concurrency: row.concurrency ?? 1,
        throughput: throughput(benchmark, row),
        latency: row.request_duration_ms ?? undefined,
      });
    }
  }
  return [...datasets.values()]
    .map((dataset) => {
      const photonModels = new Set(
        dataset.measurements
          .filter((row) => row.engine === "photon")
          .map((row) => row.modelId),
      );
      return {
        ...dataset,
        models: dataset.models.filter((model) => photonModels.has(model.id)),
        measurements: dataset.measurements.filter((row) =>
          photonModels.has(row.modelId),
        ),
        results: dataset.results.filter((result) =>
          photonModels.has(result.model),
        ),
      };
    })
    .filter((dataset) => dataset.models.length > 0);
}

export function throughputSummary(benchmarks: BenchmarkDataset[]) {
  const multiples: number[] = [];
  const engines = new Set<string>();
  for (const benchmark of benchmarks) {
    for (const row of benchmark.measurements) {
      if (
        row.engine === "photon" ||
        row.throughput === undefined ||
        row.throughput <= 0
      )
        continue;
      const photon = benchmark.measurements.find(
        (other) =>
          other.engine === "photon" &&
          other.modelId === row.modelId &&
          other.concurrency === row.concurrency,
      );
      if (photon?.throughput !== undefined) {
        multiples.push(photon.throughput / row.throughput);
        engines.add(row.engine);
      }
    }
  }
  if (!multiples.length) return null;
  multiples.sort((a, b) => a - b);
  const middle = Math.floor(multiples.length / 2);
  return {
    count: multiples.length,
    wins: multiples.filter((value) => value > 1).length,
    median:
      multiples.length % 2
        ? multiples[middle]
        : (multiples[middle - 1] + multiples[middle]) / 2,
    min: multiples[0],
    max: multiples[multiples.length - 1],
    engines: [...engines],
  };
}

const HEADLINE_THROUGHPUT_THRESHOLD = 1.2;

export const isHeadlineSpeedup = (multiple: number) =>
  Number(multiple.toFixed(2)) > HEADLINE_THROUGHPUT_THRESHOLD;

export function throughputHeadline(benchmarks: BenchmarkDataset[]) {
  const summary = throughputSummary(
    benchmarks.filter((row) => row.modality === "vision"),
  );
  const multiple = summary ? Number(summary.max.toFixed(2)) : 0;
  if (!summary || !isHeadlineSpeedup(multiple))
    return "Built for fast inference.";
  return `Up to ${multiple} times faster than competing inference engines.`;
}
